import React, { useEffect, useState } from 'react'

import { VideoPlayer } from '../../atoms/video-player'
import { useWindowSize } from '../../hooks/use-window-size.hook'
import { SlideContent } from './carousal.types'

interface SlideVideoProps {
  content: SlideContent
  index: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  feedItemRef: any
  setCurrentVideoIndex: React.Dispatch<React.SetStateAction<number>>
  hideControlBars?: boolean
  showVideos?: boolean
  fullWidth?: boolean
}

/**
 * @param {JSON} Content should contain elements in SlideContent interface.
 * @param {any} feedItemRef We have to pass it through props to pause the video when next slide appears.
 * @param {number} index
 * @param {setStateDispatch} setCurrentVideoIndex/Pause state of the video
 * @param {boolean} hideControlBars show/hide control bars in the video
 * @param {boolean} showVideos Determines if the video should be playable and user sees the bigPlayButton (button in the middle)
 * @returns
 */
function SlideVideo({
  fullWidth = false,
  content,
  index,
  feedItemRef,
  setCurrentVideoIndex,
  hideControlBars,
  showVideos,
}: SlideVideoProps) {
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const screenSize = useWindowSize()

  useEffect(() => {
    if (screenSize && screenSize.width <= 475) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
  }, [screenSize])

  useEffect(() => {
    if (feedItemRef?.current?.[index]) {
      feedItemRef.current[index].pause()
    }
  }, [feedItemRef, index])

  const handleClick = () => {
    if (showVideos) {
      setCurrentVideoIndex(index)
      if (feedItemRef?.current?.[index]) {
        feedItemRef.current[index].play()
      }
    }
  }

  // If showVideos is false, show thumbnail only (for content card)
  if (!showVideos) {
    return (
      <div
        onClick={handleClick}
        key={`${index}-${content.id}-${content.campaignContentId}`}
        className={`${fullWidth ? "custom-carousel-video h-full w-full" : ""} m-0 object-fill relative`}
      >
        {/* Show thumbnail image instead of video player */}
        <div className='h-inherit flex flex-grow items-center justify-center w-full bg-light-gray relative'>
          <img
            loading="lazy"
            src={content.thumbnail || content.mediaUrl || ''}
            className="!object-contain w-full h-full"
            alt={content.answerText || content.mediaUrl || 'Video thumbnail'}
          />
          
          {/* Custom play button overlay that matches video-react styling */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div 
              className="custom-play-button"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
              }}
            >
              <div 
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '12px solid white',
                  borderTop: '8px solid transparent',
                  borderBottom: '8px solid transparent',
                  marginLeft: '4px'
                }}
              />
            </div>
          </div>

          {/* Black bars at bottom to match video player styling */}
          <div className="absolute bottom-0 left-0 right-0 h-12"></div>
        </div>
      </div>
    )
  }

  // If showVideos is true, show actual video player (for modal)
  return (
    <div
      onClick={!showVideos ? handleClick : undefined}
      key={`${index}-${content.id}-${content.campaignContentId}`}
      className={`${fullWidth ? "custom-carousel-video h-full w-full" : ""} m-0 object-fill`}
    >
      <VideoPlayer
        width={350}
        height={438}
        index={index}
        desktop={isMobile}
        videoRef={feedItemRef}
        src={content.mediaUrl as string}
        poster={content.thumbnail as string}
        hideControlBars={hideControlBars}
        disableBigPlayButton={false}
        key={`${index} ${content.id} ${content.campaignContentId}`}
      />
    </div>
  )
}

export default SlideVideo
